import React, { useState, useEffect } from 'react';
import { fmtDec, MetricCard } from '../utils';

const OWNER_MAP = {
  'peter':   ['peter povey', 'peter'],
  'mitch':   ['mitch jankowski', 'mitch', 'mitch jankowsk'],
  'dan':     ['dan pena', 'dan'],
  'sarah':   ['sarah sutter', 'sarah'],
  'jared':   ['jared gavard', 'jared'],
  'justin':  ['justin gavard', 'justin'],
  'barry':   ['barry-lynn', 'barry'],
  'kelsey':  ['kelsey gavard', 'kelsey'],
};

function ownerMatches(ownerName, repId) {
  if (!ownerName || !repId) return false;
  const lower = ownerName.toLowerCase().trim();
  const aliases = OWNER_MAP[repId] || [];
  return aliases.some(a => lower.includes(a) || a.includes(lower));
}

function formatDate(str) {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch(e) { return str; }
}

function isOverdue(dueAt) {
  if (!dueAt) return false;
  return new Date(dueAt) < new Date();
}

const STATUS_COLORS = {
  'folsom | ready for delivery':          { bg: '#ecfdf5', color: '#059669' },
  'folsom | order ready to pickup':       { bg: '#ecfdf5', color: '#059669' },
  'loomis | order ready to pick up':      { bg: '#ecfdf5', color: '#059669' },
  'reminder - order ready to pick up':    { bg: '#fffbeb', color: '#d97706' },
  'final payment request sent':           { bg: '#fef2f2', color: '#dc2626' },
  'payment | reminder sent':              { bg: '#fef2f2', color: '#dc2626' },
  'payment | reminder - late fee added':  { bg: '#fef2f2', color: '#dc2626' },
};

function statusStyle(status) {
  const key = (status || '').toLowerCase().trim();
  for (const [k, v] of Object.entries(STATUS_COLORS)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  return { bg: '#f3f4f6', color: '#6b7280' };
}

export default function UnpaidInvoices({ user, isAdmin }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    fetch('/.netlify/functions/printavo')
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setInvoices(data.unpaidInvoices || []);
        setFetchedAt(data.fetchedAt);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 16 }}>
      <div className="spinner" />
      <span style={{ color: 'var(--text2)', fontSize: 13 }}>Fetching unpaid invoices from Printavo...</span>
    </div>
  );

  if (error) return (
    <div>
      <div className="rep-welcome"><h2>Unpaid invoices</h2></div>
      <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '12px 16px', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
        Error connecting to Printavo: {error}
      </div>
    </div>
  );

  // Filter by rep unless admin
  let filtered = isAdmin ? invoices : invoices.filter(inv => ownerMatches(inv.owner, user.repId));

  // Apply filters
  const statuses = [...new Set(invoices.map(i => i.status))].sort();
  const owners   = [...new Set(invoices.map(i => i.owner))].filter(Boolean).sort();

  if (statusFilter !== 'all') filtered = filtered.filter(i => i.status === statusFilter);
  if (isAdmin && ownerFilter !== 'all') filtered = filtered.filter(i => ownerMatches(i.owner, ownerFilter));

  const totalOutstanding = filtered.reduce((s, i) => s + i.outstanding, 0);
  const overdue = filtered.filter(i => isOverdue(i.dueAt));

  const selectStyle = { fontSize: 12, padding: '4px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontFamily: 'var(--font)', color: 'var(--text2)', background: 'var(--surface)' };

  return (
    <div>
      <div className="rep-welcome">
        <h2>{isAdmin ? 'Unpaid invoices — all reps' : 'Your unpaid invoices'}</h2>
        <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
          Live from Printavo {fetchedAt ? '· last fetched ' + new Date(fetchedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
        </p>
      </div>

      <div className="metrics">
        <MetricCard label="Unpaid invoices" value={filtered.length} sub="matching statuses" />
        <MetricCard label="Total outstanding" value={fmtDec(totalOutstanding)} sub="across all invoices" valueStyle={{ color: 'var(--red)' }} />
        <MetricCard label="Overdue" value={overdue.length} sub="past due date" valueStyle={overdue.length > 0 ? { color: 'var(--red)' } : {}} />
        <MetricCard label="Avg outstanding" value={filtered.length > 0 ? fmtDec(totalOutstanding / filtered.length) : '—'} sub="per invoice" />
      </div>

      <div className="table-card" style={{ marginTop: 20 }}>
        <div className="table-header">
          <span className="table-header-title">Unpaid invoices</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {isAdmin && (
              <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} style={selectStyle}>
                <option value="all">All reps</option>
                {Object.keys(OWNER_MAP).map(id => <option key={id} value={id}>{id.charAt(0).toUpperCase() + id.slice(1)}</option>)}
              </select>
            )}
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
              <option value="all">All statuses</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="table-wrap">
          {filtered.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              No unpaid invoices found 🎉
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Order nickname</th>
                  {isAdmin && <th>Rep</th>}
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Due date</th>
                  <th style={{ textAlign: 'right' }}>Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv, i) => {
                  const style = statusStyle(inv.status);
                  const overdue = isOverdue(inv.dueAt);
                  return (
                    <tr key={i}>
                      <td style={{ fontFamily: 'var(--mono)', fontWeight: 500, color: 'var(--brand)' }}>
                        <a href={`https://jg-graphics.printavo.com/orders/${inv.id}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)', textDecoration: 'none' }}>
                          #{inv.orderNumber}
                        </a>
                      </td>
                      <td style={{ fontWeight: 500 }}>{inv.customer}</td>
                      <td style={{ color: 'var(--text2)', maxWidth: 260, whiteSpace: 'normal', fontSize: 12 }}>{inv.nickname}</td>
                      {isAdmin && <td style={{ color: 'var(--text2)' }}>{inv.owner}</td>}
                      <td>
                        <span style={{ background: style.bg, color: style.color, fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                          {inv.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12, color: overdue ? 'var(--red)' : 'var(--text2)', fontWeight: overdue ? 600 : 400 }}>
                        {formatDate(inv.dueAt)}{overdue ? ' ⚠' : ''}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 600, color: 'var(--red)' }}>
                        {fmtDec(inv.outstanding)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
